import {
  CalendarBlank,
  DownloadSimple,
  FlowArrow,
  Folder,
  Plus,
  Trash,
  X,
} from '@phosphor-icons/react';
import {
  Button,
  Checkbox,
  cn,
  ConfirmDialog,
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
  SearchField,
  toast,
  Toggle,
} from '@reelplicate/ui';
import { type ComponentType, useId, useState } from 'react';

import { BulkTag } from './bulk-tags';
import { useDownload } from './download';
import { collectionItems } from './library';
import { Covers } from './library-panel';
import { useMedia } from './media-context';
import { useMediaOutlet } from './media-outlet';
import type { Asset, Collection, SmartRule } from './types';

const action =
  'text-label-sm text-text-inverse flex items-center gap-1.5 rounded-md px-2.5 py-[7px] outline-none hover:bg-[rgba(248,249,239,0.12)] focus-visible:shadow-focus-ring';

function BarButton({
  icon: Icon,
  label,
  onClick,
  disabled,
  className,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(action, 'disabled:opacity-50', className)}
    >
      <Icon aria-hidden className="size-4" />
      {label}
    </button>
  );
}

type BulkBarProps = {
  selectedIds: string[];
  onClear: () => void;
  /** For a new smart collection: what "matches the current filters" means. */
  smartRule: SmartRule;
};

const soon = (what: string) => () => toast(`${what} is coming soon`);

/**
 * Figma "Media / Bulk bar" (184:1453): floats over the library while items
 * are selected. Always shows the count.
 */
export function BulkBar({ selectedIds, onClear, smartRule }: BulkBarProps) {
  const { trash, assets, workspaceId } = useMedia();
  const downloads = useDownload(workspaceId);
  const [confirming, setConfirming] = useState(false);
  const count = selectedIds.length;
  const items = `${count} ${count === 1 ? 'item' : 'items'}`;

  return (
    <div
      role="toolbar"
      aria-label="Selection"
      className="bg-bg-inverse sticky bottom-6 z-20 mx-auto flex w-fit max-w-full flex-wrap items-center gap-1 rounded-[14px] py-2 pr-2 pl-4 drop-shadow-[0_8px_12px_rgba(0,0,0,0.2)]"
    >
      <span
        aria-live="polite"
        className="text-label-md text-text-inverse whitespace-nowrap"
      >
        {count} selected
      </span>
      <span aria-hidden className="mx-1 h-5 w-px bg-[rgba(248,249,239,0.2)]" />
      <AddToCollection selectedIds={selectedIds} smartRule={smartRule} />
      <BulkTag selectedIds={selectedIds} triggerClassName={action} />
      <BarButton icon={FlowArrow} label="Use in flow" onClick={soon('Flows')} />
      <BarButton icon={CalendarBlank} label="Schedule" onClick={soon('Scheduling')} />
      <BarButton
        icon={DownloadSimple}
        label={downloads.pending ? 'Downloading…' : 'Download'}
        disabled={downloads.pending}
        onClick={() =>
          void downloads.download(
            assets.filter((asset) => selectedIds.includes(asset.id)),
          )
        }
      />
      <BarButton icon={Trash} label="Delete" onClick={() => setConfirming(true)} />
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Delete ${items}?`}
        description="Their files are deleted too, and they leave every collection. This can’t be undone."
        confirmLabel={`Delete ${items}`}
        onConfirm={() => {
          trash(selectedIds);
          onClear();
          toast(`Deleted ${items}`);
        }}
      />
      <button
        type="button"
        aria-label="Clear selection"
        onClick={onClear}
        className={cn(action, 'p-[7px]')}
      >
        <X aria-hidden className="size-4" />
      </button>
    </div>
  );
}

type Change = 'add' | 'remove';

/**
 * What the confirm button says and the toast after it: "Add to X",
 * "Remove from X", "Update 2 collections"…
 */
function describeChanges(
  adds: Collection[],
  removes: Collection[],
  count: number,
): { label: string; done: string } {
  const [only] = [...adds, ...removes];
  const total = adds.length + removes.length;
  if (removes.length === 0) {
    return total === 1
      ? { label: `Add to ${only!.name}`, done: `Added ${count} to “${only!.name}”` }
      : {
          label: `Add to ${total} collections`,
          done: `Added ${count} to ${total} collections`,
        };
  }
  if (adds.length === 0) {
    return total === 1
      ? {
          label: `Remove from ${only!.name}`,
          done: `Removed ${count} from “${only!.name}”`,
        }
      : {
          label: `Remove from ${total} collections`,
          done: `Removed ${count} from ${total} collections`,
        };
  }
  return { label: `Update ${total} collections`, done: `Updated ${total} collections` };
}

/**
 * Figma "Popover · Add to collection" (187:1001). Ticking a collection puts
 * the whole selection in it; unticking one it's (partly) in takes it out.
 * Nothing is saved until the confirm button.
 */
function AddToCollection({
  selectedIds,
  smartRule,
}: {
  selectedIds: string[];
  smartRule: SmartRule;
}) {
  const {
    assets,
    collections,
    addToCollection,
    removeManyFromCollection,
    createCollection,
  } = useMedia();
  const { editSmartCollection } = useMediaOutlet();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  /** Keyed by collection id. */
  const [draft, setDraft] = useState<Map<string, Change>>(new Map());
  const [smart, setSmart] = useState(false);
  const smartId = useId();

  const count = selectedIds.length;
  const handPicked = collections.filter((c) => !c.smart);
  const name = query.trim();
  // Matches first; the rest stay listed so a new name can sit beside them.
  const matchesName = (c: Collection) =>
    c.name.toLowerCase().includes(name.toLowerCase());
  const shown = [
    ...handPicked.filter(matchesName),
    ...handPicked.filter((c) => !matchesName(c)),
  ];
  const canCreate =
    name.length > 0 &&
    !handPicked.some((c) => c.name.toLowerCase() === name.toLowerCase());

  const reset = () => {
    setQuery('');
    setDraft(new Map());
    setSmart(false);
  };
  const close = () => {
    setOpen(false);
    reset();
  };

  const create = async () => {
    if (smart) {
      // A smart collection fills itself: finish its rules in the builder.
      close();
      editSmartCollection({ initial: { name, rule: smartRule } });
      return;
    }
    const created = await createCollection(name, { assetIds: selectedIds });
    if (!created) return;
    toast.success(`Added ${count} to “${created.name}”`);
    close();
  };

  const changed = (change: Change) =>
    handPicked.filter((c) => draft.get(c.id) === change);
  const adds = changed('add');
  const removes = changed('remove');
  const changes = draft.size > 0 ? describeChanges(adds, removes, count) : undefined;

  const confirm = () => {
    if (!changes) {
      if (canCreate) void create();
      return;
    }
    adds.forEach((c) => addToCollection(c.id, selectedIds));
    removes.forEach((c) => removeManyFromCollection(c.id, selectedIds));
    toast.success(changes.done);
    close();
  };

  const confirmLabel = changes?.label ?? (canCreate ? `Create “${name}”` : 'Add');

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <PopoverTrigger asChild>
        <button type="button" className={cn(action, 'bg-[rgba(248,249,239,0.12)]')}>
          <Folder aria-hidden className="size-4" />
          Add to collection
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        aria-label="Add to collection"
        className="rounded-[14px] [&_[data-slot=popover-body]]:p-0"
        footer={
          <>
            <PopoverClose asChild>
              <Button variant="ghost" size="sm">
                Cancel
              </Button>
            </PopoverClose>
            <Button size="sm" disabled={draft.size === 0 && !canCreate} onClick={confirm}>
              {confirmLabel}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2 px-3.5 pt-3.5 pb-2.5">
          <p className="text-label-md text-text-primary">
            Add {count} {count === 1 ? 'item' : 'items'} to…
          </p>
          <SearchField
            aria-label="Find or name a collection"
            placeholder="Find or create a collection"
            shortcut={false}
            value={query}
            onValueChange={setQuery}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canCreate) {
                e.preventDefault();
                void create();
              }
            }}
          />
        </div>
        <ul aria-label="Collections" className="flex flex-col px-1.5">
          {shown.map((c) => (
            <CollectionOption
              key={c.id}
              collection={c}
              items={collectionItems(c, assets)}
              selectedIds={selectedIds}
              change={draft.get(c.id)}
              onChange={(change) =>
                setDraft((prev) => {
                  const next = new Map(prev);
                  if (change) next.set(c.id, change);
                  else next.delete(c.id);
                  return next;
                })
              }
            />
          ))}
          {canCreate && (
            <li>
              <button
                type="button"
                onClick={() => void create()}
                className="bg-brand-subtle text-label-sm text-text-brand focus-visible:shadow-focus-ring flex w-full items-center gap-2 rounded-md p-2.5 text-left outline-none"
              >
                <Plus aria-hidden className="size-4" />
                <span className="flex-1">Create collection “{name}”</span>
                <span aria-hidden className="text-caption text-text-tertiary">
                  ↵
                </span>
              </button>
            </li>
          )}
        </ul>
        <div className="border-border-subtle mt-1.5 flex items-start gap-2.5 border-t px-3.5 py-3">
          <Toggle
            id={smartId}
            checked={smart}
            onCheckedChange={setSmart}
            disabled={!canCreate}
            aria-describedby={`${smartId}-hint`}
          />
          <label htmlFor={smartId} className="flex flex-col gap-0.5">
            <span className="text-label-sm text-text-primary">Make it smart</span>
            <span id={`${smartId}-hint`} className="text-caption text-text-secondary">
              Pick up anything that matches the current filters, in the rule builder.
            </span>
          </label>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CollectionOption({
  collection,
  items,
  selectedIds,
  change,
  onChange,
}: {
  collection: Collection;
  items: Asset[];
  selectedIds: string[];
  change: Change | undefined;
  onChange: (change: Change | undefined) => void;
}) {
  const already = selectedIds.filter((id) => collection.assetIds?.includes(id)).length;
  const all = already === selectedIds.length;
  const original = all ? true : already > 0 ? 'indeterminate' : false;
  const checked = change === 'add' ? true : change === 'remove' ? false : original;
  const id = useId();
  return (
    <li className="hover:bg-bg-subtle flex items-center gap-2 rounded-md px-2">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(next) => {
          if (next === true) onChange(original === true ? undefined : 'add');
          else onChange(original === false ? undefined : 'remove');
        }}
      />
      <label
        htmlFor={id}
        className="flex min-w-0 flex-1 items-center gap-2.5 px-2.5 py-1.5"
      >
        <Covers assets={items} />
        <span className="flex min-w-0 flex-col">
          <span className="text-body-sm-strong text-text-primary truncate">
            {collection.name}
          </span>
          <span className="text-caption text-text-tertiary truncate">
            {items.length} items
            {change === 'remove'
              ? ` · ${all ? 'all' : already} will come out`
              : already > 0 && ` · ${all ? 'all' : already} already in it`}
          </span>
        </span>
      </label>
    </li>
  );
}
